import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Container,
  Fade,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  selectClient,
  selectCurrentRoleClientId,
  selectMe,
} from "store/selectors";
import { ProtectedPaths, UnprotectedPaths } from "routes";
import { indigo } from "@mui/material/colors"; // @ts-ignore
import Logo from "assets/images/logo.svg?react";
import TabsMenu from "./TabsMenu";
import { TabModel } from "api/models/TabModel";
import AvatarButton from "./AvatarButton";
import Dropdown from "./Dropdown";
import NotificationsBell from "./NotificationsBell";
import { getClientInfoFromRoles } from "../../helpers/clientInfoFromRoles";
import { fetchClient } from "../../store/appSlice";

export type MenuItemType =
  | { label: string; path: ProtectedPaths | string; submenu?: undefined }
  | {
      label: string;
      submenu: { label: string; path?: ProtectedPaths; link?: string }[];
      path?: undefined;
    };

interface HeaderProps {
  tabs: TabModel[];
}

const Header = ({ tabs }: HeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname.replace("/", "");
  const dispatch = useAppDispatch();

  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const me = useAppSelector(selectMe);
  const currentRoleClientId = useAppSelector(selectCurrentRoleClientId);
  const client = useAppSelector(selectClient);

  useEffect(() => {
    if (me?.roles) {
      const isUserAdmin =
        me.roles.find((d) => d.isCurrentRole)?.name === "ROLE_ADMIN";
      setIsAdmin(isUserAdmin);
    }
  }, [me]);

  useEffect(() => {
    if (currentRoleClientId) {
      dispatch(fetchClient(currentRoleClientId));
    }
  }, [currentRoleClientId]);

  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorElSubMenu, setAnchorElSubMenu] = useState<null | HTMLElement>(
    null,
  );
  const [subMenuOpen, setSubMenuOpen] = useState<null | string>(null);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleOpenSubMenu = (
    label: string,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setAnchorElSubMenu(event.currentTarget);
    setSubMenuOpen(label);
  };
  const handleCloseNavMenu = () => {};
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  const handleCloseSubMenu = () => {
    setAnchorElSubMenu(null);
    setSubMenuOpen(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/login";
  };

  useEffect(() => {
    if (me && me.roles.length === 0) {
      navigate(`/${UnprotectedPaths.Unauthorized}`);
    }
  }, [me, navigate]);

  const adminMenuItems = [
    {
      label: "Overview",
      submenu: [
        { label: "Summary", path: ProtectedPaths.OverviewSummary },
        { label: "Charts", path: ProtectedPaths.OverviewCharts },
      ],
    },
    { label: "Reports", path: ProtectedPaths.Readings },
    {
      label: "Forecasts",
      submenu: [
        { label: "Production", path: ProtectedPaths.Production },
        { label: "Consumption", path: ProtectedPaths.Consumption },
        { label: "Batteries", path: ProtectedPaths.Batteries },
      ],
    },
    {
      label: "Nord Pool",
      submenu: [
        { label: "Supply plans", path: ProtectedPaths.NordPoolSupplyPlans },
        { label: "Portfolios", path: ProtectedPaths.Portfolios },
        { label: "Results", path: ProtectedPaths.NordPoolResults },
        { label: "Balance plans", path: ProtectedPaths.BalancePlans },
        { label: "IDC/Elbas", path: ProtectedPaths.NordPoolIdc },
        { label: "Settings", path: ProtectedPaths.NordPoolSettings },
      ],
    },
    {
      label: "GO",
      submenu: [
        { label: "Summary", path: ProtectedPaths.GuaranteesOfOriginSummary },
        {
          label: "Certificates",
          path: ProtectedPaths.GuaranteesOfOriginCertificates,
        },
      ],
    },
    {
      label: "Registers",
      path: ProtectedPaths.Clients,
    },
    {
      label: "Settings",
      path: ProtectedPaths.Users,
    },
  ];

  const clientMenuItems = [
    {
      label: "Reports",
      submenu: [
        { label: "Summary", path: ProtectedPaths.ClientReportsSummary },
        { label: "Forecast", path: ProtectedPaths.ClientReportsForecast },
      ],
    },
    {
      label: "Company",
      path: `${ProtectedPaths.Clients}/${currentRoleClientId}`,
    },
    ...(client?.contractType === "FIXED" || client?.contractType === "EXTERNAL"
      ? [{ label: "Supply plans", path: ProtectedPaths.SupplyPlanOrders }]
      : []),
    { label: "Registers", path: ProtectedPaths.MeteringPoints },
  ];

  const menuItems: MenuItemType[] =
    me && me.roles.length > 0
      ? getClientInfoFromRoles(me.roles).isAdmin
        ? adminMenuItems
        : clientMenuItems
      : [];

  const renderMenuItems = (items: MenuItemType[]) =>
    items.map((page) => (
      <React.Fragment key={`Page-${page.label}`}>
        <MenuItem
          key={page.label}
          onClick={(event) => {
            if (page.submenu) {
              handleOpenSubMenu(page.label, event);
            } else {
              navigate(`/${page.path}`);
            }
          }}
          selected={currentPath === page.path}
        >
          <Typography textAlign="center">{page.label}</Typography>
        </MenuItem>
        {page.submenu && (
          <Menu
            id="submenu-appbar"
            anchorEl={anchorElSubMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            keepMounted
            open={subMenuOpen === page.label}
            onClose={handleCloseSubMenu}
            TransitionComponent={Fade}
            sx={{ mt: 1 }}
          >
            {page.submenu.map((subPage) => (
              <MenuItem
                key={subPage.label}
                onClick={() => {
                  navigate(
                    subPage.link ? `/${subPage.link}` : `/${subPage.path}`,
                  );
                  handleCloseSubMenu();
                }}
                selected={currentPath === subPage.path}
              >
                <Typography textAlign="center">{subPage.label}</Typography>
              </MenuItem>
            ))}
          </Menu>
        )}
      </React.Fragment>
    ));

  return (
    <>
      <AppBar position="static" sx={{ bgcolor: indigo[900] }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            <Box sx={{ mr: 2 }}>
              <Logo />
            </Box>
            <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              ></IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                TransitionComponent={Fade}
                sx={{ display: { xs: "flex", md: "none" } }}
              ></Menu>
            </Box>
            <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
              {renderMenuItems(menuItems)}
            </Box>
            <NotificationsBell />
            {me && (
              <Box sx={{ flexGrow: 0 }}>
                <AvatarButton me={me} handleOpenUserMenu={handleOpenUserMenu} />
                <Dropdown
                  anchorElUser={anchorElUser}
                  handleCloseUserMenu={handleCloseUserMenu}
                  handleLogout={handleLogout}
                  roles={me.roles}
                  meId={me.id}
                  navigate={navigate}
                />
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>
      {tabs && (
        <TabsMenu currentPath={currentPath} isAdmin={isAdmin} tabs={tabs} />
      )}
    </>
  );
};

export default Header;
